import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { cx } from '../cx.js';

export type ToastTone = 'neutral' | 'success' | 'error';

export interface ToastOptions {
  message: ReactNode;
  tone?: ToastTone;
  /** 自动消失时间，默认 4000 毫秒 */
  durationMs?: number;
}

interface ToastItem extends Required<Omit<ToastOptions, 'message'>> {
  id: number;
  message: ReactNode;
}

interface ToastContextValue {
  show: (options: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * 结果标记：成功用"成功"令牌（不用状态色"运行中"，D-P0-09-24）；失败即"异常"，用异常红。颜色只是辅助，结果以文字播报。
 */
const TONE_MARK: Record<ToastTone, string> = {
  neutral: 'bg-fg-muted',
  success: 'bg-success',
  error: 'bg-status-danger',
};

/**
 * 位置：在底部 Tab（无底栏时为底部安全区）与主操作栏之上，高度取外壳与 PrimaryActionBar 实测写入的变量，
 * 系统字号放大时一起变化，不压住底栏与主按钮（PRD §8.7「焦点与键盘」、200% 不重叠）。
 */
const TOAST_BOTTOM = 'bottom-[calc(max(var(--nx-bottom-nav-h,0px),env(safe-area-inset-bottom))+var(--nx-action-bar-h,0px)+0.75rem)]';

export interface ToastProviderProps {
  children: ReactNode;
  className?: string;
}

/** 轻提示。结果以一句话播报（PRD §8.7「读屏」）。 */
export function ToastProvider({ children, className }: ToastProviderProps) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    setItems((list) => list.filter((item) => item.id !== id));
  }, []);

  const show = useCallback(
    ({ message, tone = 'neutral', durationMs = 4000 }: ToastOptions) => {
      const id = nextId.current++;
      setItems((list) => [...list, { id, message, tone, durationMs }]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), durationMs),
      );
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const value = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        data-toast-region=""
        className={cx('pointer-events-none fixed inset-x-0 z-[60] flex flex-col items-center gap-2 px-4', TOAST_BOTTOM, className)}
      >
        {items.map((item) => (
          <div
            key={item.id}
            role={item.tone === 'error' ? 'alert' : 'status'}
            data-tone={item.tone}
            className="pointer-events-auto flex max-w-md animate-fade-in items-center gap-2 rounded-xl bg-surface px-4 py-3 text-sm text-fg shadow-lg ring-1 ring-border"
          >
            <span aria-hidden="true" className={cx('h-2 w-2 shrink-0 rounded-full', TONE_MARK[item.tone])} />
            <span>{item.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast must be used inside <ToastProvider>');
  return value;
}
